import * as React from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    DialogTitle,
    DialogContent,
    Button,
    DialogActions,
} from '@mui/material';
import pxToRem from 'assets/theme/function/px-to-rem';
import { memo, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { CreateProductPriceRequest } from 'types/product/product-price';
import { useProductPricePaymentModeQuery } from 'store/slices/product/product-enum';
import { useRuleProductListQuery } from 'store/slices/rule/rule-entities-enum';
import rgba from 'assets/theme/function/rgba';
import colors from 'assets/theme/base/colors';
import DialogIconClose from 'component/dialog-icon-close';
import {
    useUpdateProductPriceMutation,
    useProductPriceDataQuery,
} from 'store/slices/product/product-price';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import SuccessSnackbarEffect from 'component/success-snackbar';

interface Props {
    currencyList?: string[];
    productId?: string;
    productPriceId: string;
    setOpenDialogEditProductPrice: React.Dispatch<
        React.SetStateAction<boolean>
    >;
    getPriceList: () => void;
}

const EditProductPrice: React.FC<Props> = memo((props: Props) => {
    const {
        currencyList,
        productId,
        productPriceId,
        setOpenDialogEditProductPrice,
        getPriceList,
    } = props;

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm<CreateProductPriceRequest>({
        defaultValues: {
            currency: '',
            paymentMode: '',
            price: 0,
            discountPercent: 0,
            ruleId: '',
        },
    });
    const { data: dataPaymentMod } = useProductPricePaymentModeQuery();
    const { data: ruleProductList } = useRuleProductListQuery({
        entity: 'product-price',
    });

    const [updateProductPriceApi, { isSuccess, error, isError }] =
        useUpdateProductPriceMutation();

    const { data } = useProductPriceDataQuery(productPriceId);

    const formRef = useRef<HTMLFormElement>(null);

    const dispatch = useAppDispatch();

    const onSubmit = (values: CreateProductPriceRequest) => {
        updateProductPriceApi({
            ...values,
            productPriceId,
            ruleId: values.ruleId ? values.ruleId : undefined,
            price: Number(values.price),
        });
    };

    const closeDialog = () => {
        setOpenDialogEditProductPrice(false);
    };

    useEffect(() => {
        if (data) {
            reset({
                currency: data.currency || '',
                paymentMode: data.paymentMode || '',
                price: data.price || 0,
                discountPercent: data.discountPercent || 0,
                ruleId: data.ruleId || '',
            });
        }
    }, [data, reset]);

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
        if (isSuccess) {
            dispatch(
                openSnackbar({
                    message: productId
                        ? 'Product Price Update'
                        : 'Product Price Created',
                    severity: 'info',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
            closeDialog();
            getPriceList();
        }
    }, [isError, isSuccess]);

    return (
        <>
            <Box
                display={'flex'}
                justifyContent={'center'}
                bgcolor={rgba(colors.primary.main, 0.5)}
            >
                <DialogTitle textTransform={'uppercase'}>
                    EDITE PRICE
                </DialogTitle>
                <DialogIconClose handleClose={closeDialog} />
            </Box>

            <DialogContent>
                <Box
                    component="form"
                    ref={formRef}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Box display={'flex'} pt={pxToRem(15)}>
                        <FormControl sx={{ ml: pxToRem(10) }} fullWidth>
                            <InputLabel>Currency</InputLabel>
                            <Controller
                                name="currency"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label="Currency"
                                        {...field}
                                        inputProps={{
                                            'aria-label': 'Currency',
                                        }}
                                    >
                                        {currencyList?.map((category) => (
                                            <MenuItem
                                                key={category.toString()}
                                                value={category.toString()}
                                            >
                                                {category}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                        </FormControl>

                        <FormControl sx={{ ml: pxToRem(10) }} fullWidth>
                            <InputLabel>Payment Mode</InputLabel>
                            <Controller
                                name="paymentMode"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label="Payment Mode"
                                        {...field}
                                        inputProps={{
                                            'aria-label': 'Payment Mode',
                                        }}
                                    >
                                        {dataPaymentMod?.map((category) => (
                                            <MenuItem
                                                key={category.toString()}
                                                value={category.toString()}
                                            >
                                                {category}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                        </FormControl>
                    </Box>

                    <Box display={'flex'} pt={pxToRem(15)}>
                        <Controller
                            name="price"
                            control={control}
                            rules={{
                                required: 'Price Title is required',
                                pattern: {
                                    value: /^[0-9]*[.,]?[0-9]*$/,
                                    message: 'Only numbers are allowed',
                                },
                            }}
                            render={({ field: { name, value, onChange } }) => (
                                <TextField
                                    sx={{ ml: pxToRem(10) }}
                                    fullWidth
                                    name={name}
                                    value={value ?? ''}
                                    onChange={(e) => {
                                        let val = e.target.value;
                                        val = val.replace(',', '.');
                                        if (/^[0-9]*\.?[0-9]*$/.test(val)) {
                                            onChange(val);
                                        }
                                    }}
                                    label="Price"
                                    error={Boolean(errors[name])}
                                    helperText={
                                        errors[name]?.message?.toString() || ''
                                    }
                                    slotProps={{
                                        input: {
                                            inputMode: 'decimal',
                                        },
                                    }}
                                />
                            )}
                        />

                        <Controller
                            name="discountPercent"
                            control={control}
                            rules={{
                                required: 'Discount Percent Title is required',
                            }}
                            render={({ field }) => (
                                <TextField
                                    sx={{ ml: pxToRem(10) }}
                                    fullWidth
                                    {...field}
                                    value={field.value ?? ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        field.onChange(
                                            value === '' ? '' : Number(value),
                                        );
                                    }}
                                    label="Discount Percent"
                                />
                            )}
                        />
                    </Box>

                    <Box display={'flex'} pt={pxToRem(15)}>
                        <FormControl sx={{ ml: pxToRem(10) }} fullWidth>
                            <InputLabel>Rule Id</InputLabel>
                            <Controller
                                name="ruleId"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label="Rule Id"
                                        {...field}
                                        inputProps={{
                                            'aria-label': 'Rule Id',
                                        }}
                                    >
                                        {ruleProductList?.map((category) => (
                                            <MenuItem
                                                key={category.id.toString()}
                                                value={category.id.toString()}
                                            >
                                                {category.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                        </FormControl>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    sx={{ width: '50%' }}
                    onClick={() => formRef.current?.requestSubmit()}
                    variant={'contained'}
                >
                    Product Price Update
                </Button>
                <Button
                    sx={{ width: '50%', bgcolor: colors.action.menuSelect }}
                    onClick={() => closeDialog()}
                    variant={'contained'}
                >
                    Cancel
                </Button>
            </DialogActions>
            <SuccessSnackbarEffect
                isSuccess={isSuccess}
                message={'Successfully Edit an Product Price!'}
            />
        </>
    );
});

export default EditProductPrice;
